// Accordion Configuration
const ACCORDION_CONFIG = {
  ANIMATION_DURATION: 400,
  SCROLL_OFFSET: 120,
  ANIMATION_TIMEOUT_BUFFER: 50, // Fallback wenn transitionend nicht feuert
  SELECTORS: {
    panel: '.Accordion .etx-accordion__content, .etx-accordion__content',
    list: '.etx-accordion-list',
    accordion: '.etx-accordion',
    header: '.etx-accordion__header[data-accordion-clickable]',
    content: '.etx-accordion__content',
  },
};

// Accordion Manager Class
class AccordionManager {
  constructor(config = ACCORDION_CONFIG) {
    this.config = config;
    this.animatingPanels = new WeakSet();
    this.activeTimeouts = new Set();
    this.eventListeners = [];
    this.initialized = false;
  }

  // Animation State Management
  isAnimating(panel) {
    return this.animatingPanels.has(panel);
  }

  lock(panel) {
    this.animatingPanels.add(panel);
  }

  unlock(panel) {
    this.animatingPanels.delete(panel);
  }

  // Measure panel height
  measurePanelHeight(panel) {
    const prevHeight = panel.style.height;
    const prevTransition = panel.style.transition;
    
    panel.style.transition = 'none';
    panel.style.height = 'auto';
    const height = panel.getBoundingClientRect().height;
    panel.style.height = prevHeight;
    
    // Force reflow
    panel.offsetHeight;
    panel.style.transition = prevTransition;
    
    return height;
  }

  // Open panel with animation
  openPanel(accordion, panel, duration = this.config.ANIMATION_DURATION) {
    if (!panel || this.isAnimating(panel)) return;
    
    const targetHeight = this.measurePanelHeight(panel);
    this.lock(panel);

    panel.style.transition = duration > 0 ? `height ${duration}ms ease` : 'none';
    panel.style.height = '0px';

    requestAnimationFrame(() => {
      panel.style.height = `${targetHeight}px`;
    });

    // Cleanup function
    const cleanup = () => {
      if (panel.style.height === `${targetHeight}px`) {
        panel.style.transition = '';
        panel.style.height = 'auto';
      }
      this.unlock(panel);
      panel.removeEventListener('transitionend', onTransitionEnd);
    };

    const onTransitionEnd = (e) => {
      if (e.target === panel && e.propertyName === 'height') {
        clearTimeout(fallbackTimeout);
        cleanup();
      }
    };

    if (duration > 0) {
      panel.addEventListener('transitionend', onTransitionEnd);
      
      // Fallback timeout
      const fallbackTimeout = setTimeout(() => {
        panel.removeEventListener('transitionend', onTransitionEnd);
        cleanup();
      }, duration + this.config.ANIMATION_TIMEOUT_BUFFER);
      
      this.activeTimeouts.add(fallbackTimeout);
    } else {
      // No animation
      cleanup();
    }
  }

  // Close panel with animation
  closePanel(accordion, panel, duration = this.config.ANIMATION_DURATION) {
    if (!panel || this.isAnimating(panel)) return;

    const currentHeight = panel.style.height === 'auto' 
      ? this.measurePanelHeight(panel) 
      : panel.getBoundingClientRect().height;

    this.lock(panel);
    panel.style.transition = duration > 0 ? `height ${duration}ms ease` : 'none';
    panel.style.height = `${currentHeight}px`;

    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });

    // Cleanup function
    const cleanup = () => {
      panel.style.transition = '';
      this.unlock(panel);
      panel.removeEventListener('transitionend', onTransitionEnd);
    };

    const onTransitionEnd = (e) => {
      if (e.target === panel && e.propertyName === 'height') {
        clearTimeout(fallbackTimeout);
        cleanup();
      }
    };

    if (duration > 0) {
      panel.addEventListener('transitionend', onTransitionEnd);
      
      // Fallback timeout
      const fallbackTimeout = setTimeout(() => {
        panel.removeEventListener('transitionend', onTransitionEnd);
        cleanup();
      }, duration + this.config.ANIMATION_TIMEOUT_BUFFER);
      
      this.activeTimeouts.add(fallbackTimeout);
    } else {
      // No animation
      cleanup();
    }
  }

  // Close other accordions in list (for single-open mode)
  closeOthers(list, currentAccordion) {
    const activeAccordions = list.querySelectorAll(`${this.config.SELECTORS.accordion}.active`);
    
    activeAccordions.forEach(accordion => {
      if (accordion === currentAccordion) return;
      
      const panel = accordion.querySelector(this.config.SELECTORS.content);
      if (panel) {
        accordion.classList.remove('active');
        this.closePanel(accordion, panel);
      }
    });
  }

  // Handle accordion click
  handleAccordionClick(event) {
    const header = event.target.closest(this.config.SELECTORS.header);
    if (!header) return;

    const accordion = header.closest(this.config.SELECTORS.accordion);
    const list = header.closest(this.config.SELECTORS.list);
    
    if (!accordion || !list) return;

    const panel = accordion.querySelector(this.config.SELECTORS.content);
    if (!panel || this.isAnimating(panel)) return;

    const isActive = accordion.classList.contains('active');
    const allowMultiple = list.hasAttribute('data-accordion-multiple');
    const shouldScrollUp = list.hasAttribute('data-accordion-scrollup');

    if (isActive) {
      // Close current accordion
      accordion.classList.remove('active');
      this.closePanel(accordion, panel);
    } else {
      // Open current accordion
      if (!allowMultiple) {
        this.closeOthers(list, accordion);
      }
      
      accordion.classList.add('active');
      this.openPanel(accordion, panel);

      // Scroll to accordion if needed
      if (shouldScrollUp) {
        const scrollDelay = this.config.ANIMATION_DURATION + this.config.ANIMATION_TIMEOUT_BUFFER;
        const timeoutId = setTimeout(() => {
          const offset = accordion.getBoundingClientRect().top + window.scrollY - this.config.SCROLL_OFFSET;
          window.scrollTo({ top: offset, behavior: 'smooth' });
          this.activeTimeouts.delete(timeoutId);
        }, scrollDelay);
        
        this.activeTimeouts.add(timeoutId);
      }
    }
  }

  // Initialize all accordions
  initialize() {
    if (this.initialized) {
      console.warn('etxAccordion already initialized');
      return;
    }

    console.log('etxAccordion initialised');

    // Initialize all panels
    const panels = document.querySelectorAll(this.config.SELECTORS.panel);
    panels.forEach(panel => {
      panel.style.height = panel.classList.contains('is-open') ? 'auto' : '0px';
      panel.style.overflow = 'hidden';
    });

    // Open first accordion in lists with data-accordion-opened
    document.querySelectorAll(`${this.config.SELECTORS.list}[data-accordion-opened]`).forEach(list => {
      const firstAccordion = list.querySelector(this.config.SELECTORS.accordion);
      if (firstAccordion) {
        const panel = firstAccordion.querySelector(this.config.SELECTORS.content);
        if (panel) {
          firstAccordion.classList.add('active');
          this.openPanel(firstAccordion, panel, 0); // No animation on init
        }
      }
    });

    // Use event delegation for better performance
    const boundHandler = this.handleAccordionClick.bind(this);
    document.addEventListener('click', boundHandler);
    
    this.eventListeners.push({
      target: document,
      type: 'click',
      handler: boundHandler,
    });

    this.initialized = true;
  }

  // Cleanup function
  destroy() {
    // Remove all event listeners
    this.eventListeners.forEach(({ target, type, handler }) => {
      target.removeEventListener(type, handler);
    });
    this.eventListeners = [];

    // Clear all timeouts
    this.activeTimeouts.forEach(clearTimeout);
    this.activeTimeouts.clear();

    // Clear animation locks
    this.animatingPanels = new WeakSet();

    this.initialized = false;
    console.log('etxAccordion destroyed');
  }
}

// Main function to initialize accordion
function etxAccordion() {
  // Use singleton pattern
  if (!window.etxAccordionManager) {
    window.etxAccordionManager = new AccordionManager();
  }
  
  window.etxAccordionManager.initialize();
  
  return window.etxAccordionManager;
}

// Export for external use
window.etxAccordion = etxAccordion;